import React from "react";
import { HStack } from "@design-system/HStack/HStack";
import { Switch, type SwitchSize } from "@design-system/Switch/Switch";
import { SwitchIndicator } from "@design-system/Switch/SwitchIndicator";
import { VStack } from "@design-system/VStack/VStack";
import checkboxStyles from "./CheckboxPlayground.module.scss";
import switchStyles from "./SwitchPlayground.module.scss";

/**
 * @purpose The Switch playground's static sections: every state at both sizes
 * (laid out to sit next to Figma `3132:750`), label placement, and the
 * non-interactive `SwitchIndicator`.
 */

interface MatrixStateProps {
    disabled?: boolean;
    loading?: boolean;
    invalid?: boolean;
    readOnly?: boolean;
    "data-forced-state"?: "hover" | "pressed" | "focus";
}

const matrixStates: ReadonlyArray<{
    id: string;
    label: string;
    props: MatrixStateProps;
}> = [
    { id: "normal", label: "Normal", props: {} },
    { id: "hover", label: "Hover", props: { "data-forced-state": "hover" } },
    {
        id: "pressed",
        label: "Pressed",
        props: { "data-forced-state": "pressed" },
    },
    { id: "focus", label: "Focus", props: { "data-forced-state": "focus" } },
    { id: "disabled", label: "Disabled", props: { disabled: true } },
    { id: "loading", label: "Loading", props: { loading: true } },
    { id: "invalid", label: "Invalid", props: { invalid: true } },
    { id: "readOnly", label: "Read-only", props: { readOnly: true } },
];

const matrixColumns: ReadonlyArray<{
    id: string;
    size: SwitchSize;
    isChecked: boolean;
}> = [
    { id: "sm-off", size: "sm", isChecked: false },
    { id: "sm-on", size: "sm", isChecked: true },
    { id: "md-off", size: "md", isChecked: false },
    { id: "md-on", size: "md", isChecked: true },
];

export const SwitchMatrix = () => (
    <VStack className={checkboxStyles.section} gap={24} padding={24}>
        <h2 className={checkboxStyles.sectionTitle}>State matrix</h2>
        <div className={switchStyles.grid}>
            <span className={checkboxStyles.columnHeader}>State</span>
            {matrixColumns.map((column) => (
                <span key={column.id} className={checkboxStyles.columnHeader}>
                    {`${column.size} · ${column.isChecked ? "on" : "off"}`}
                </span>
            ))}
            {matrixStates.map((state) => (
                <React.Fragment key={state.id}>
                    <span className={checkboxStyles.rowLabel}>
                        {state.label}
                    </span>
                    {matrixColumns.map((column) => (
                        <div
                            key={column.id}
                            className={checkboxStyles.matrixCell}
                        >
                            <Switch
                                {...state.props}
                                checked={column.isChecked}
                                onCheckedChange={() => undefined}
                                size={column.size}
                                aria-label={`${state.label} ${column.id}`}
                            />
                        </div>
                    ))}
                </React.Fragment>
            ))}
        </div>
    </VStack>
);

export const SwitchLabelExamples = () => (
    <VStack className={checkboxStyles.section} gap={24} padding={24}>
        <h2 className={checkboxStyles.sectionTitle}>Label placement</h2>
        <HStack gap={40} align="start" wrap>
            <VStack gap={8}>
                <span className={checkboxStyles.exampleLabel}>
                    end (default, hugs its content)
                </span>
                <Switch
                    checked
                    onCheckedChange={() => undefined}
                    label="Email me a weekly summary"
                />
                <Switch
                    checked={false}
                    onCheckedChange={() => undefined}
                    size="sm"
                    label="Compact row"
                />
            </VStack>
            <VStack gap={8}>
                <span className={checkboxStyles.exampleLabel}>
                    start (full-width row, track at the end)
                </span>
                <VStack className={switchStyles.labelStartExample} gap={8}>
                    <Switch
                        checked
                        onCheckedChange={() => undefined}
                        label="Attach PDF"
                        labelPlacement="start"
                    />
                    <Switch
                        checked={false}
                        onCheckedChange={() => undefined}
                        label="Send a copy to me"
                        labelPlacement="start"
                    />
                </VStack>
            </VStack>
        </HStack>
    </VStack>
);

const indicatorSizes = ["sm", "md"] as const satisfies readonly SwitchSize[];

export const SwitchIndicators = () => (
    <VStack className={checkboxStyles.section} gap={16} padding={24}>
        <VStack gap={4}>
            <h2 className={checkboxStyles.sectionTitle}>Static indicators</h2>
            <span className={checkboxStyles.sectionDescription}>
                Non-interactive switch visuals for rows that toggle themselves
                (Picker toggle items, filter rows).
            </span>
        </VStack>
        <HStack gap={32} align="start" wrap>
            {indicatorSizes.map((size) =>
                [false, true].map((isChecked) => (
                    <React.Fragment key={`${size}-${String(isChecked)}`}>
                        <VStack gap={8}>
                            <SwitchIndicator checked={isChecked} size={size} />
                            <span className={checkboxStyles.exampleLabel}>
                                {`${size} ${isChecked ? "on" : "off"}`}
                            </span>
                        </VStack>
                        <VStack gap={8}>
                            <SwitchIndicator
                                checked={isChecked}
                                size={size}
                                disabled
                            />
                            <span className={checkboxStyles.exampleLabel}>
                                {`${size} ${isChecked ? "on" : "off"} disabled`}
                            </span>
                        </VStack>
                    </React.Fragment>
                )),
            )}
        </HStack>
    </VStack>
);
